/**
 * Bottom-centre skill cluster (Aion-style): a circular class crest (level, gold below it) with a
 * thin red HP bar extending left and the class resource bar extending right ("cur/max" above
 * each), player buffs / debuffs flanking the crest under the bars, the main skill row
 * (1–6 rotation | Q / E cooldowns | R / F + the round G / Shift+F slot, then the round Dodge
 * (Space) slot at the bar end), an
 * optional consumables row, the player cast bar above everything, the proc row (proc, transform
 * and stack-counted buffs with stacks and timers) floating above the cast bar, the screen-side
 * spell activation overlay (procOverlay.ts, optional) and the XP hairline along the bottom edge.
 *
 * Replaces the legacy action bar, player cast bar and player frame (widgets.ts). The latency
 * probe contract lives in the ActionBar (`.actionbar` / `.action`) and the cast bar
 * (`.player-cast.active`); press feedback goes through `flashActionBind`.
 *
 * Per frame: kit primitives diff their own state; everything here caches the last written value
 * and touches the DOM only on change. No layout reads, no allocations in steady state.
 */
import type { ContentRegistry, DeepReadonly, SimEvent, Unit, UnitId } from '@keystone/sim';
import type { HudHandlers, HudUpdate } from '../hud';
import type { HudWidget } from '../widgets';
import { hudNow } from '../../game/prediction';
import { ActionBar } from '../actionBar';
import { DodgeSlot } from './dodgeSlot';
import { el, setText } from '../dom';
import { IconSlot } from '../kit/iconSlot';
import { StatBar, groupNumber } from '../kit/statBar';
import { AuraRow } from '../kit/auraIcon';
import { crestSvg } from '../kit/glyphs';
import { roundButton, type RoundButton } from '../kit/roundButton';
import { intText } from '../kit/tokens';
import { absorbTotal } from '../unitFrame';
import { inRunView } from '../../meta/inrunHud';
import { currencyGlyphSvg } from '../../meta/ui/glyphs';
import { PlayerCastBar } from './castBar';
import { isProcRowAura } from './derive';
import { ProcOverlay } from './procOverlay';
import { procOverlaySettings } from './procOverlaySettings';
import './cluster.css';

/** Most pips the secondary-resource row draws (Holy Power 3, room for talents). */
const MAX_PIPS = 6;
const CONSUMABLE_SLOTS = 4;

export class SkillCluster implements HudWidget {
  readonly root: HTMLElement;
  readonly actionBar: ActionBar;
  readonly castBar: PlayerCastBar;
  readonly dodge: DodgeSlot;
  private readonly hp: StatBar;
  private readonly res: StatBar;
  private readonly pipsBox: HTMLElement;
  private readonly pips: HTMLElement[] = [];
  private readonly buffs: AuraRow;
  /** Proc / transform / stack buffs, centred above the cast bar (out of the flow: no layout shift). */
  private readonly procs: AuraRow;
  private readonly overlay: ProcOverlay | null;
  private readonly unsubOverlay: (() => void) | null;
  private readonly debuffs: AuraRow;
  private readonly consumables: HTMLElement;
  readonly consumableSlots: IconSlot[] = [];
  private readonly crest: HTMLElement;
  private crestArt: SVGSVGElement;
  private readonly crestLevel: HTMLElement;
  private readonly crestSub: HTMLElement;
  private readonly goldText: HTMLElement;
  private readonly combat: RoundButton;
  private readonly xp: HTMLElement;
  private readonly xpFill: HTMLElement;
  private readonly xpGain: HTMLElement;
  // Last written state.
  private playerId: UnitId | null = null;
  private hasPlayer = true;
  private classId: string | null | undefined = undefined;
  private resId = '';
  private resSegs = -1;
  private pipMax = -1;
  private pipLit = -1;
  private dead = false;
  private inCombat = false;
  private low = false;
  private viewVersion = -1;
  private progressOn: boolean | null = null;
  private goldShown = -1;

  constructor(host: HTMLElement, handlers: Pick<HudHandlers, 'bind' | 'dodge'>, overlayHost: HTMLElement | null = null) {
    this.root = el('div', 'hkc', host);
    this.procs = new AuraRow(this.root, { max: 6, size: 'sm', className: 'hkc-procs', filter: (_i, def) => isProcRowAura(def) });
    this.castBar = new PlayerCastBar(this.root);
    const overlay = overlayHost ? new ProcOverlay(overlayHost) : null;
    this.overlay = overlay;
    this.unsubOverlay = overlay ? procOverlaySettings.subscribe((on) => overlay.setEnabled(on)) : null;

    const body = el('div', 'hkc-body', this.root);
    const left = el('div', 'hkc-side left', body);
    this.hp = new StatBar(left, { dir: 'rtl', text: 'cur/max', numbers: 'full', extra: true, className: 'hkc-hp' });
    const lflank = el('div', 'hkc-flank', left);
    this.consumables = el('div', 'hkc-consumables hk-hidden', lflank);
    for (let i = 0; i < CONSUMABLE_SLOTS; i++) this.consumableSlots.push(new IconSlot(this.consumables, { size: 'sm', className: 'hkc-consumable' }));
    this.buffs = new AuraRow(lflank, { max: 8, size: 'xs', className: 'hkc-buffs', filter: (_i, def) => !!def && def.kind === 'buff' && !def.flags?.hidden && !isProcRowAura(def) });

    this.crest = el('div', 'hk-crest hkc-crest', body);
    this.crestArt = this.crest.appendChild(crestSvg(null));
    this.crestLevel = el('div', 'hk-crest-level hkc-level', this.crest);
    this.crestSub = el('div', 'hk-crest-sub hkc-gold', this.crest);
    this.crestSub.title = 'Gold (this run’s kill rewards are added live)';
    this.crestSub.appendChild(currencyGlyphSvg('gold'));
    this.goldText = el('span', 'hk-num', this.crestSub);

    const right = el('div', 'hkc-side right', body);
    this.res = new StatBar(right, { dir: 'ltr', text: 'cur/max', numbers: 'full', ghost: false, className: 'hkc-res' });
    this.pipsBox = el('div', 'hkc-pips', right);
    for (let i = 0; i < MAX_PIPS; i++) this.pips.push(el('i', 'hkc-pip', this.pipsBox));
    const rflank = el('div', 'hkc-flank', right);
    this.debuffs = new AuraRow(rflank, { max: 8, size: 'xs', className: 'hkc-debuffs', filter: (_i, def) => !!def && def.kind === 'debuff' && !def.flags?.hidden });

    const main = el('div', 'hkc-main', this.root);
    this.combat = roundButton(main, { icon: 'swords', size: 'md', label: 'Combat state', className: 'hkc-combat' });
    this.actionBar = new ActionBar(main, handlers.bind, { size: 'md', roundBinds: ['G'] });
    this.dodge = new DodgeSlot(main, () => handlers.dodge?.());

    this.xp = el('div', 'hkc-xp hk-hit', this.root);
    const track = el('div', 'hkc-xp-track', this.xp);
    this.xpFill = el('div', 'hkc-xp-fill', track);
    this.xpGain = el('div', 'hkc-xp-gain', track);
  }

  /** Show the (future) consumables row above the bars' left flank. */
  setConsumablesVisible(on: boolean): void {
    this.consumables.classList.toggle('hk-hidden', !on);
  }

  ingest(events: readonly SimEvent[], playerId: UnitId | null): void {
    this.castBar.ingest(events, playerId, typeof performance !== 'undefined' ? performance.now() : 0);
  }

  update(h: HudUpdate, realNow: number): void {
    const { world, content } = h;
    const u = h.playerId ? world.units[h.playerId] : undefined;
    const has = !!u;
    if (has !== this.hasPlayer) {
      this.hasPlayer = has;
      this.root.classList.toggle('no-player', !has);
    }
    this.castBar.update(u, world, content, realNow);
    if (!u || !h.playerId) {
      this.overlay?.update(undefined, 0);
      this.drawProgress();
      return;
    }
    const swapped = h.playerId !== this.playerId;
    this.playerId = h.playerId;
    if (u.classId !== this.classId) this.setClass(u.classId);

    // HP (+ absorb overlay) and the class resource.
    if (swapped) this.hp.snap(u.hp, u.maxHp, realNow, absorbTotal(u));
    else this.hp.update(u.hp, u.maxHp, realNow, absorbTotal(u));
    this.drawResources(u, content, realNow);

    const dead = !u.alive;
    if (dead !== this.dead) {
      this.dead = dead;
      this.root.classList.toggle('dead', dead);
    }
    const low = u.alive && u.hp < u.maxHp * 0.3;
    if (low !== this.low) {
      this.low = low;
      this.root.classList.toggle('low-hp', low);
    }
    if (u.inCombat !== this.inCombat) {
      this.inCombat = u.inCombat;
      this.combat.setActive(u.inCombat);
      this.combat.root.classList.toggle('in-combat', u.inCombat);
    }

    const now = hudNow(world);
    this.procs.update(world, content, u, h.playerId, now);
    this.overlay?.update(u, now);
    this.buffs.update(world, content, u, h.playerId, now);
    this.debuffs.update(world, content, u, h.playerId, now);
    this.actionBar.update(world, content, h.queries, h.playerId, h.selectedId, h.hoverId);
    this.dodge.update(world, content, h.queries, h.playerId);
    this.drawProgress();
  }

  private setClass(classId: string | null): void {
    this.classId = classId;
    const art = crestSvg(classId);
    this.crest.replaceChild(art, this.crestArt);
    this.crestArt = art;
    this.crest.dataset['class'] = classId ?? '';
  }

  private drawResources(u: DeepReadonly<Unit>, content: ContentRegistry, realNow: number): void {
    const cls = u.classId ? content.class(u.classId) : undefined;
    const primary = cls?.resources[0]?.id ?? '';
    const pool = primary ? u.resources[primary] : undefined;
    if (primary !== this.resId) {
      if (this.resId) this.root.classList.remove(`res-${this.resId}`);
      this.resId = primary;
      if (primary) this.root.classList.add(`res-${primary}`);
    }
    // Small pools (combo-style) draw as a segmented bar.
    const segs = pool && pool.max <= MAX_PIPS ? pool.max : 0;
    if (segs !== this.resSegs) {
      this.resSegs = segs;
      this.res.setSegments(segs);
    }
    if (pool) this.res.update(pool.cur, pool.max, realNow);
    else this.res.update(0, 1, realNow);
    // Secondary small resource (Holy Power) as diamond pips over the resource bar.
    const second = cls?.resources[1];
    const sp = second ? u.resources[second.id] : undefined;
    const pmax = sp && sp.max <= MAX_PIPS ? sp.max : 0;
    const plit = sp ? Math.max(0, Math.min(pmax, Math.floor(sp.cur))) : 0;
    if (pmax !== this.pipMax) {
      this.pipMax = pmax;
      for (let i = 0; i < MAX_PIPS; i++) this.pips[i]!.classList.toggle('hk-hidden', i >= pmax);
      this.pipsBox.classList.toggle('hk-hidden', pmax === 0);
      this.pipLit = -1;
    }
    if (plit !== this.pipLit) {
      this.pipLit = plit;
      for (let i = 0; i < pmax; i++) this.pips[i]!.classList.toggle('lit', i < plit);
      this.pipsBox.classList.toggle('full', pmax > 0 && plit >= pmax);
    }
  }

  /** Crest level, gold under the crest and the XP hairline (from meta/inrunHud inRunView). */
  private drawProgress(): void {
    const v = inRunView;
    if (v.active !== this.progressOn) {
      this.progressOn = v.active;
      this.root.classList.toggle('no-progress', !v.active);
      if (!v.active) {
        setText(this.crestLevel, '');
        this.goldShown = -1;
      }
      this.viewVersion = -1;
    }
    if (!v.active) return;
    const gold = Math.round(v.gold);
    if (gold !== this.goldShown) {
      this.goldShown = gold;
      this.goldText.textContent = groupNumber(gold);
      this.crestSub.classList.toggle('ticking', gold !== v.goldTarget);
    }
    if (v.version === this.viewVersion) return;
    this.viewVersion = v.version;
    setText(this.crestLevel, intText(v.level));
    const next = v.next > 0 ? v.next : 1;
    const base = v.maxed ? 1 : Math.min(1, Math.max(0, v.startXp / next));
    const gain = v.maxed ? 0 : Math.min(1 - base, Math.max(0, (v.xp - v.startXp) / next));
    this.xpFill.style.transform = `scaleX(${base})`;
    this.xpGain.style.transform = `translateX(${base * 100}%) scaleX(${gain})`;
    // Scaled about its left edge, then moved to where the pre-run XP ends (% of its own full width).
    this.xp.title = v.maxed
      ? `Level ${v.level}${v.heroName ? ` · ${v.heroName}` : ''} · max level`
      : `Level ${v.level}${v.heroName ? ` · ${v.heroName}` : ''} · ${groupNumber(v.xp)} / ${groupNumber(v.next)} XP${v.runXp > 0 ? ` (+${groupNumber(v.runXp)} this run)` : ''}`;
  }

  dispose(): void {
    this.dodge.dispose();
    this.unsubOverlay?.();
    this.overlay?.dispose();
    this.root.remove();
  }
}
